import { IonLabel } from '@ionic/react';
import { orderBy } from 'lodash';
import { ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plan, PlanEntitlement } from '../../../api/graphql/generated';
import { i18nKeyType } from '../../../i18n';
import { PlanFeatureData, PlanFeatureDetails } from '../../plan/plan.types';
import './subscription-plan-features.css';

export interface PlanFeature {
  entitlement?: PlanEntitlement;
  featureData: PlanFeatureData;
}

interface SubscriptionPlanCardFeaturesProps {
  /**
   * The plan whose details to display.
   */
  plan: Plan;

  /**
   * The maximum number of features to display before truncating the list.
   */
  showMoreLimit?: number;

  overrideFeatures?: PlanFeature[];
}

const SubscriptionPlanCardFeatures = (
  props: SubscriptionPlanCardFeaturesProps
): ReactElement => {
  const { plan, overrideFeatures } = props;
  const showMoreLimit = props.showMoreLimit ?? 3;
  const { i18n } = useTranslation();

  const [isShowingMore, setIsShowingMore] = useState(false);

  const features: PlanFeature[] = useMemo(() => {
    const results: PlanFeature[] =
      overrideFeatures ??
      (plan.entitlements
        ?.map((entitlement) => {
          const details =
            PlanFeatureDetails[
              entitlement.key as keyof typeof PlanFeatureDetails
            ];

          if (details?.highlight) {
            return {
              entitlement,
              featureData: details,
            };
          }
        })
        .filter((d) => d?.featureData !== undefined) as PlanFeature[]) ??
      [];

    return orderBy(results, ['featureData.highlight.order'], ['asc']);
  }, [plan, overrideFeatures]);

  const featuresToShow = useMemo(() => {
    if (features.length >= showMoreLimit && !isShowingMore) {
      return features.slice(0, showMoreLimit);
    }

    return features;
  }, [features, showMoreLimit, isShowingMore]);

  return (
    <>
      {featuresToShow.length > 0 && (
        <>
          <ul>
            {featuresToShow.map((feature, index) => {
              let value = feature.entitlement?.value;

              if (feature.featureData.valueFormatter) {
                value = feature.featureData.valueFormatter(value);
              }

              return (
                <li key={index}>
                  {i18n.t(
                    feature.featureData.highlight?.titleKey as i18nKeyType,
                    {
                      value,
                    }
                  )}
                </li>
              );
            })}
          </ul>
          {features.length > showMoreLimit && (
            <div className="show-more-container">
              <div className="show-more-line" />
              <div
                className="show-more-text"
                onClick={() => setIsShowingMore(!isShowingMore)}
              >
                <IonLabel>
                  {isShowingMore
                    ? i18n.t('common:show_less')
                    : i18n.t('common:show_more')}
                </IonLabel>
              </div>
              <div className="show-more-line" />
            </div>
          )}
        </>
      )}
    </>
  );
};

export { SubscriptionPlanCardFeatures };
